import { AdditionalYields } from '../../../types'
import {
  CometInterestRateIndexes,
  CompoundV3GeneralPublicResponse,
} from '../../../types/lender/compound-v3-types'
import {
  getCompoundV3Assets,
  LENDER_MODE_NO_MODE,
  toGenericPriceKey,
  toOracleKey,
} from '../../../assets'
import { getCompoundV3BaseAsset } from '../addresses/compoundV3'
import { parseRawAmount, SECONDS_PER_YEAR } from '../../../utils/parsing'
import { GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'

const COMET_RISK_DECIMALS = 18
const SECONDS_PER_DAY = 3600 * 24
const DAYS_IN_YEAR = 365
export const COMP_ADDRESSES: { [chainId: string]: string } = {
  '1': '0xc00e94cb662c3520282e6f5717214004a7f26888',
  '10': '0x7e7d4467112689329f7e06571ed0e8cbad4910ee',
  '130': '0xdf78e4f0a8279942ca68046476919a90f2288656',
  '137': '0x8505b9d2254a7ae468c0e9dd10ccea3a837aef5c',
  '5000': '0x52b7d8851d6ccbc6342ba0855be65f7b82a3f17f',
  '8453': '0x9e1028f5f1d5ede59748ffcee5532509976840e0',
  '42161': '0x354a6da3fcde098f8389cad84b0182725c6c91de',
  '534352': '0x643e160a3c3e2b7eae198f0beb1bfd2441450e86',
}
export const getCompoundV3ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => CompoundV3GeneralPublicResponse | undefined, number] => {
  const assetsToQuery = getCompoundV3Assets(chainId, lender)
  const baseAssetData = getCompoundV3BaseAsset(lender, chainId)
  const tokensNoBase = assetsToQuery.filter(
    (a) => a !== baseAssetData.baseAsset,
  )

  const baseLength = tokensNoBase.length * 2
  // 6 comet-level reads (rewards/supply/borrow/interest) + store-front factor
  // + baseBorrowMin
  const expectedNumberOfCalls = baseLength + 8
  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      // Comet store-front price factor (WAD). Liquidator discount on a
      // collateral asset ≈ storeFrontPriceFactor * (1 - liquidationFactor).
      const rawStoreFront = data[baseLength + 6]
      const storeFrontPriceFactor =
        rawStoreFront === undefined || rawStoreFront === '0x'
          ? 0
          : Number(
              parseRawAmount(rawStoreFront.toString(), COMET_RISK_DECIMALS),
            )

      // Minimum borrow, raw base-asset units. A borrow below it REVERTS, so a
      // UI that offers an amount under this is offering a transaction that
      // cannot land.
      const rawBaseBorrowMin = data[baseLength + 7]
      const baseBorrowMin =
        rawBaseBorrowMin === undefined || rawBaseBorrowMin === '0x'
          ? undefined
          : String(rawBaseBorrowMin)

      let result: any = {}
      for (let i = 0; i < tokensNoBase.length; i++) {
        const asset = tokensNoBase[i]
        const resultsAssetInfo = data[i * 2]
        const resultsTotals = data[i * 2 + 1]
        const assetMeta = tokenList[asset]
        const decimals = assetMeta?.decimals ?? 18
        const totals = parseRawAmount(resultsTotals[0], decimals)
        const oracleKey =
          toOracleKey(assetMeta?.assetGroup) ??
          toGenericPriceKey(asset, chainId)

        const totalUsd = Number(totals) * (prices[oracleKey] ?? 0)

        const marketUid = createMarketUid(chainId, lender, asset)

        // Comet liquidation penalty for this collateral asset:
        // storeFrontPriceFactor * (1 - liquidationFactor), all WAD factors.
        const liquidationFactor = Number(
          parseRawAmount(
            resultsAssetInfo.liquidationFactor.toString(),
            COMET_RISK_DECIMALS,
          ),
        )
        const liquidationPenalty = Math.max(
          storeFrontPriceFactor * (1 - liquidationFactor),
          0,
        )

        result[asset] = {
          marketUid,
          name: 'Collateral ' + (assetMeta?.symbol ?? ''),
          poolId: asset,
          underlying: asset,
          asset: assetMeta,
          config: {
            [LENDER_MODE_NO_MODE]: {
              category: LENDER_MODE_NO_MODE,
              // collateral factors
              borrowCollateralFactor: parseRawAmount(
                resultsAssetInfo.borrowCollateralFactor.toString(),
                COMET_RISK_DECIMALS,
              ),
              collateralFactor: parseRawAmount(
                resultsAssetInfo.liquidateCollateralFactor.toString(),
                COMET_RISK_DECIMALS,
              ),
              borrowFactor: 1,
              liquidationPenalty,
              closeFactor: 1,
              collateralDisabled: false,
              debtDisabled: true,
            },
          },
          // Comet absorbs the entire account on liquidation → full close factor.
          closeFactor: 1,
          supplyCap: parseRawAmount(
            resultsAssetInfo.supplyCap.toString(),
            decimals,
          ),

          // interest rates
          variableBorrowRate: 0,
          stableBorrowRate: 0,
          depositRate: 0,
          intrinsicYield: additionalYields?.intrinsicYields?.[oracleKey] ?? 0,

          utilization: 0,
          // debt and liquidity are zero
          totalDebt: 0,
          totalDebtUSD: 0,
          totalLiquidity: totals,
          borrowLiquidity: totals,
          totalLiquidityUSD: totalUsd,
          borrowLiquidityUSD: totalUsd,
          // deposits are different outputs for base asset and others
          totalDeposits: totals,
          totalDepositsUSD: totalUsd,
          // rewards
          rewards: [],
          collateralActive: true,
          isActive: true,
          isFrozen: false,
          borrowingEnabled: false,
          depositsEnabled: true,
        }
      }

      const baseMeta = tokenList[baseAssetData.baseAsset]

      // parse data for rewards calculation
      const decimals = baseMeta?.decimals ?? 18
      const resultsSupplyRewards = Number(data[baseLength]?.toString())
      const resultsBorrowRewards = Number(data[baseLength + 1]?.toString())
      const resultsIndexScale = data[baseLength + 2]?.toString()
      const resultsSupply = parseRawAmount(
        data[baseLength + 3]?.toString(),
        decimals,
      )
      const resultsBorrow = parseRawAmount(
        data[baseLength + 4]?.toString(),
        decimals,
      )

      const compToSuppliersPerDay =
        (resultsSupplyRewards / Number(resultsIndexScale)) * SECONDS_PER_DAY
      const compToBorrowersPerDay =
        (resultsBorrowRewards / Number(resultsIndexScale)) * SECONDS_PER_DAY

      const resultsInterest = data[baseLength + 5]

      const oracleKeyBase =
        toOracleKey(baseMeta?.assetGroup) ??
        toGenericPriceKey(baseAssetData.baseAsset, chainId)
      const price = prices[oracleKeyBase] ?? 1
      const liquidity = Number(resultsSupply) - Number(resultsBorrow)
      const compPrice = prices['COMP']

      const baseMarketUid = createMarketUid(
        chainId,
        lender,
        baseAssetData.baseAsset,
      )

      const baseData = {
        [baseAssetData.baseAsset]: {
          marketUid: baseMarketUid,
          name: 'Loan ' + (baseMeta?.symbol ?? ''),
          poolId: baseAssetData.baseAsset,
          underlying: baseAssetData.baseAsset,
          asset: baseMeta,
          // collateral factots
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          supplyCap: 0,

          config: {
            [LENDER_MODE_NO_MODE]: {
              category: LENDER_MODE_NO_MODE,
              // collateral factors
              borrowCollateralFactor: 0,
              collateralFactor: 0,
              borrowFactor: 1,
              // base asset is the borrowable, never seized as collateral
              liquidationPenalty: 0,
              closeFactor: 1,
              // base asset: borrowable, no collateral
              collateralDisabled: true,
              debtDisabled: false,
            },
          },
          // Comet absorbs the entire account on liquidation → full close factor.
          closeFactor: 1,
          // Minimum borrow, RAW base-asset units. Comet reverts any borrow
          // that would leave the position below it, so this is a hard gate on
          // opening — surfaced so a UI can validate before building instead
          // of letting the transaction fail.
          minBorrow: baseBorrowMin,
          // interest rates
          variableBorrowRate:
            Number(resultsInterest[CometInterestRateIndexes.borrowRate]) /
            SECONDS_PER_YEAR /
            10,
          depositRate:
            Number(resultsInterest[CometInterestRateIndexes.supplyRate]) /
            SECONDS_PER_YEAR /
            10,
          stableBorrowRate: 0,
          // Comet returns utilization scaled by 1e18; surface as a 0..1 number
          // to match the canonical PoolData shape (and the other lender outputs).
          utilization: Number(
            parseRawAmount(
              resultsInterest[CometInterestRateIndexes.utilization].toString(),
              COMET_RISK_DECIMALS,
            ),
          ),

          totalLiquidity: liquidity,
          borrowLiquidity: liquidity,
          totalLiquidityUSD: liquidity * price,
          borrowLiquidityUSD: liquidity * price,

          totalDebt: resultsBorrow,
          totalDeposits: resultsSupply,

          totalDebtUSD: Number(resultsBorrow) * price,
          totalDepositsUSD: Number(resultsSupply) * price,
          // rewards - only populate if COMP exists on this chain
          rewards: COMP_ADDRESSES[chainId]
            ? [
                {
                  asset: COMP_ADDRESSES[chainId],
                  depositRate:
                    (((compPrice * compToSuppliersPerDay) /
                      Number(resultsSupply)) *
                      DAYS_IN_YEAR *
                      100) /
                    price,
                  variableBorrowRate:
                    (((compPrice * compToBorrowersPerDay) /
                      Number(resultsBorrow)) *
                      DAYS_IN_YEAR *
                      100) /
                    price,
                  stableBorrowRate: 0,
                },
              ]
            : [],
          borrowingEnabled: true,
          depositsEnabled: true,
          collateralActive: false,
          isActive: true,
          isFrozen: false,
          intrinsicYield:
            additionalYields?.intrinsicYields?.[oracleKeyBase] ?? 0,
        },
      }
      return { data: { ...result, ...baseData }, chainId }
    },
    expectedNumberOfCalls,
  ]
}
